import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Card, CardContent } from '../ui/card';
import {
  ClipboardCheck, Loader2, Check, X, Eraser, Download, MapPin, Trash2, Plus,
} from 'lucide-react';
import SignatureCanvas from 'react-signature-canvas';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const extractErr = (e, fb = 'Échec') => {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || fb;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(x => (typeof x === 'string' ? x : (x?.msg || JSON.stringify(x)))).join(' · ');
  return JSON.stringify(d);
};

/**
 * Modal de gestion d'un bon de chargement OU déchargement
 * - Pré-rempli depuis l'opération
 * - 2 signatures embarquées (canvas signature pad) : représentant site + chauffeur
 * - GPS capté à la signature si autorisé
 */
const LoadingNoteModal = ({ open, onOpenChange, operation, type, onSuccess, entityKind = 'operation' }) => {
  const isLoading = type === 'loading';
  const sitePartyKey = isLoading ? 'shipper' : 'consignee';
  const titleAction = isLoading ? 'chargement' : 'déchargement';
  const endpointPrefix = entityKind === 'transfer' ? 'transfers' : 'operations';

  const [note, setNote] = useState(null);            // Bon courant (créé ou existant)
  const [creating, setCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  // Form fields
  const [form, setForm] = useState({
    actualDate: new Date().toISOString().slice(0, 10),
    arrivalTime: '', startTime: '', endTime: '',
    actualWeight: '', actualState: '', observations: '', operatorName: '',
  });

  // Recharge le bon existant si dispo
  const loadExisting = useCallback(async () => {
    if (!operation?.id) return;
    try {
      const r = await axios.get(`${API}/${endpointPrefix}/${operation.id}/loading-notes`);
      const found = (r.data || []).find(n => n.type === type);
      if (found) {
        setNote(found);
        setForm({
          actualDate: (found.actualDate || '').slice(0, 10) || new Date().toISOString().slice(0, 10),
          arrivalTime: found.arrivalTime || '',
          startTime: found.startTime || '',
          endTime: found.endTime || '',
          actualWeight: found.actualWeight ?? '',
          actualState: found.actualState || '',
          observations: found.observations || '',
          operatorName: found.operatorName || '',
        });
      } else {
        setNote(null);
      }
    } catch (e) { /* silent */ }
  }, [operation?.id, type]);

  useEffect(() => { if (open) loadExisting(); }, [open, loadExisting]);

  const ensureNote = async () => {
    if (note) return note;
    setCreating(true);
    try {
      const r = await axios.post(`${API}/${endpointPrefix}/${operation.id}/loading-notes`, {
        type,
        actualDate: form.actualDate,
        arrivalTime: form.arrivalTime || undefined,
        startTime: form.startTime || undefined,
        endTime: form.endTime || undefined,
        actualWeight: form.actualWeight ? parseFloat(form.actualWeight) : undefined,
        actualState: form.actualState || undefined,
        observations: form.observations || undefined,
        operatorName: form.operatorName || undefined,
      });
      setNote(r.data);
      return r.data;
    } finally { setCreating(false); }
  };

  const handleSaveDetails = async () => {
    setSaving(true);
    try {
      const current = await ensureNote();
      const r = await axios.patch(`${API}/${endpointPrefix}/${operation.id}/loading-notes/${current.id}`, {
        ...form,
        actualWeight: form.actualWeight ? parseFloat(form.actualWeight) : null,
      });
      setNote(r.data);
      toast({ title: 'Enregistré', description: 'Détails du bon mis à jour' });
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err), variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const handleDownloadPdf = async () => {
    if (!note) {
      toast({ title: 'Validation', description: 'Créez d\'abord le bon (bouton Enregistrer)', variant: 'destructive' });
      return;
    }
    try {
      const r = await axios.get(`${API}/${endpointPrefix}/${operation.id}/loading-notes/${note.id}/pdf`, { responseType: 'blob' });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${note.noteNumber}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err, 'PDF indisponible'), variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto" data-testid={`loading-note-modal-${type}`}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ClipboardCheck className="w-5 h-5 text-emerald-600" />
            Bon de {titleAction} {note ? `— ${note.noteNumber}` : '(nouveau)'}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Contexte opération (lecture seule) */}
          <Card className="bg-gray-50">
            <CardContent className="p-3 text-xs space-y-1">
              <div className="flex justify-between"><span className="text-gray-500">Réf. opération :</span><strong>{operation?.reference}</strong></div>
              <div className="flex justify-between"><span className="text-gray-500">Client :</span><span>{operation?.clientName || '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Trajet :</span><span>{operation?.departure} → {operation?.arrival}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Marchandise :</span><span>{operation?.merchandiseType || '—'} — {operation?.weight ? `${operation.weight}T` : '—'}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Véhicule :</span><span>{operation?.vehicleType || '—'} {operation?.licensePlate ? `(${operation.licensePlate})` : ''}</span></div>
            </CardContent>
          </Card>

          {/* Saisies sur site */}
          <div>
            <h3 className="font-semibold text-sm text-gray-700 mb-2">Détails sur site</h3>
            <div className="grid grid-cols-3 gap-3 text-sm">
              <div>
                <Label className="text-xs">Date</Label>
                <Input type="date" value={form.actualDate} onChange={e => setForm(f => ({ ...f, actualDate: e.target.value }))} className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Heure arrivée</Label>
                <Input type="time" value={form.arrivalTime} onChange={e => setForm(f => ({ ...f, arrivalTime: e.target.value }))} className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Heure début</Label>
                <Input type="time" value={form.startTime} onChange={e => setForm(f => ({ ...f, startTime: e.target.value }))} className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Heure fin</Label>
                <Input type="time" value={form.endTime} onChange={e => setForm(f => ({ ...f, endTime: e.target.value }))} className="h-8 text-sm" />
              </div>
              <div>
                <Label className="text-xs">Poids constaté (T)</Label>
                <Input type="number" step="any" value={form.actualWeight} onChange={e => setForm(f => ({ ...f, actualWeight: e.target.value }))} className="h-8 text-sm" placeholder={operation?.weight ?? '—'} />
              </div>
              <div>
                <Label className="text-xs">État / Réserves</Label>
                <Input value={form.actualState} onChange={e => setForm(f => ({ ...f, actualState: e.target.value }))} className="h-8 text-sm" placeholder="Bon état / Réserves" />
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Observations</Label>
                <Textarea rows={2} value={form.observations} onChange={e => setForm(f => ({ ...f, observations: e.target.value }))} className="text-sm" placeholder="Remarques sur le déroulement, anomalies, etc." />
              </div>
              <div className="col-span-3">
                <Label className="text-xs">Opérateur Baobab</Label>
                <Input value={form.operatorName} onChange={e => setForm(f => ({ ...f, operatorName: e.target.value }))} className="h-8 text-sm" placeholder="Nom du bureau opérations Baobab" />
              </div>
            </div>
          </div>

          {/* Signatures embarquées */}
          {note && (
            <div className="border-t pt-3">
              <h3 className="font-semibold text-sm text-gray-700 mb-2">Signatures embarquées (2 requises)</h3>
              <div className="grid grid-cols-2 gap-3">
                <SignatureBlock
                  label={isLoading ? "Représentant site expéditeur" : "Représentant site destinataire"}
                  party={sitePartyKey}
                  note={note}
                  operationId={operation.id}
                  endpointPrefix={endpointPrefix}
                  onSigned={(updated) => setNote(updated)}
                />
                <SignatureBlock
                  label="Chauffeur Baobab"
                  party="driver"
                  note={note}
                  operationId={operation.id}
                  endpointPrefix={endpointPrefix}
                  defaultName={operation?.driverName || ''}
                  defaultRole="Chauffeur"
                  onSigned={(updated) => setNote(updated)}
                />
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
          <Button variant="outline" onClick={handleSaveDetails} disabled={saving || creating} data-testid="save-loading-note-btn">
            {(saving || creating) && <Loader2 className="w-4 h-4 animate-spin mr-1" />}
            {note ? 'Mettre à jour les détails' : 'Enregistrer le bon'}
          </Button>
          <Button onClick={handleDownloadPdf} disabled={!note} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-loading-note-btn">
            <Download className="w-4 h-4 mr-1" /> Télécharger PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ────────────────────────────────────────────────────────────────
// Bloc signature canvas (sub-component)
// ────────────────────────────────────────────────────────────────
const SignatureBlock = ({ label, party, note, operationId, endpointPrefix = 'operations',
                         defaultName = '', defaultRole = '', onSigned }) => {
  const sigRef = useRef(null);
  const existing = (note?.signatures || []).find(s => s.party === party);
  const [name, setName] = useState(existing?.name || defaultName);
  const [role, setRole] = useState(existing?.role || defaultRole);
  const [submitting, setSubmitting] = useState(false);
  const [showPad, setShowPad] = useState(!existing);

  const handleClear = () => sigRef.current?.clear();

  const captureGps = () => new Promise((resolve) => {
    if (!navigator.geolocation) return resolve(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => resolve(null),
      { timeout: 5000, enableHighAccuracy: false }
    );
  });

  const handleSubmit = async () => {
    if (!name?.trim() || name.trim().length < 2) {
      toast({ title: 'Nom requis', description: 'Veuillez saisir le nom de la personne qui signe', variant: 'destructive' });
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      toast({ title: 'Signature manquante', description: 'Faites signer dans le cadre', variant: 'destructive' });
      return;
    }
    setSubmitting(true);
    try {
      const dataUrl = sigRef.current.toDataURL('image/png');
      const gps = await captureGps();
      const r = await axios.post(`${API}/${endpointPrefix}/${operationId}/loading-notes/${note.id}/sign`, {
        party, name: name.trim(), role: role.trim() || null,
        signatureBase64: dataUrl, gpsCoords: gps,
        deviceInfo: navigator.userAgent.slice(0, 120),
      });
      onSigned?.(r.data);
      setShowPad(false);
      toast({ title: 'Signé', description: `Signature ${label} enregistrée` });
    } catch (err) {
      toast({ title: 'Erreur', description: extractErr(err, 'Échec signature'), variant: 'destructive' });
    } finally { setSubmitting(false); }
  };

  return (
    <Card className={existing ? 'border-emerald-300 bg-emerald-50/30' : 'border-gray-200'}>
      <CardContent className="p-3 space-y-2">
        <div className="flex items-center justify-between">
          <h4 className="text-xs font-semibold text-gray-700">{label}</h4>
          {existing && (
            <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-300">
              <Check className="w-3 h-3 mr-1" /> Signé
            </Badge>
          )}
        </div>
        {existing && !showPad ? (
          <div className="space-y-1 text-xs">
            <div><strong>{existing.name}</strong>{existing.role ? ` — ${existing.role}` : ''}</div>
            <img src={existing.signatureBase64} alt="signature" className="max-h-12 border border-dashed border-gray-300 bg-white rounded" />
            {existing.gpsCoords && (
              <div className="text-[10px] text-gray-500 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {existing.gpsCoords.lat.toFixed(4)}, {existing.gpsCoords.lng.toFixed(4)}
              </div>
            )}
            <Button variant="link" size="sm" className="h-6 p-0 text-xs" onClick={() => setShowPad(true)}>
              Re-signer
            </Button>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2">
              <Input value={name} onChange={e => setName(e.target.value)} placeholder="Nom" className="h-7 text-xs" data-testid={`sig-name-${party}`} />
              <Input value={role} onChange={e => setRole(e.target.value)} placeholder="Fonction" className="h-7 text-xs" />
            </div>
            <div className="border-2 border-dashed border-gray-300 rounded bg-white">
              <SignatureCanvas
                ref={sigRef}
                penColor="#111"
                canvasProps={{
                  width: 320, height: 100,
                  className: 'w-full',
                  'data-testid': `sig-canvas-${party}`,
                }}
              />
            </div>
            <div className="flex justify-between gap-2">
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleClear}>
                <Eraser className="w-3 h-3 mr-1" /> Effacer
              </Button>
              <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
                      disabled={submitting} onClick={handleSubmit}
                      data-testid={`sig-submit-${party}`}>
                {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Check className="w-3 h-3 mr-1" />}
                Valider la signature
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default LoadingNoteModal;
